import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiUsers, 
  FiX, 
  FiSave, 
  FiEdit, 
  FiSearch, 
  FiFilter, 
  FiRefreshCw,
  FiEye,
  FiMoreVertical,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiShield
} from 'react-icons/fi';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import showToast from '../../components/Toast';
import globalRequestManager from '../../utils/globalRequestManager';
import CustomDropdown from '../../components/CustomDropdown';
import Tooltip from '../../components/Tooltip';
import './UserRoleAssignment.css';

const UserRoleAssignment = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [formData, setFormData] = useState({
    UserId: '',
    newRole: '',
    Notes: ''
  });

  const BASE_URL = config.api.baseURL;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get user's CompanyId for client-side filtering (safety measure)
      const userData = config.getUserData();
      const userCompanyId = userData?.CompanyId;
      const userRole = config.getUserRole();
      const isSuperAdmin = userRole === 'SuperAdmin' || userRole === 'superadmin';
      
      // Fetch users and roles in parallel
      const [usersRes, rolesRes] = await Promise.all([
        AxiosInstance.get(`${BASE_URL}/${config.api.apiPath}/user/all-users`),
        AxiosInstance.get(`${BASE_URL}/${config.api.apiPath}/role/all`)
      ]);

      if (usersRes.data?.statusCode === 200) {
        let usersData = usersRes.data.data || [];
        
        // Client-side filtering as safety measure (backend already filters, but double-check)
        if (!isSuperAdmin && userCompanyId) {
          usersData = usersData.filter(user => user.CompanyId === userCompanyId);
        }
        
        setUsers(usersData);
      }

      if (rolesRes.data?.statusCode === 200) {
        let rolesData = rolesRes.data.data || [];
        
        // Filter roles by company for normal users
        if (!isSuperAdmin && userCompanyId) {
          rolesData = rolesData.filter(role => role.CompanyId === userCompanyId);
        }
        
        setRoles(rolesData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Toast notification is handled by AxiosInstance interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateUserRole = async (e) => {
    e.preventDefault();
    
    if (!formData.UserId || !formData.newRole) {
      showToast.error('Please select both user and new role');
      return;
    }

    try {
      // Update user's role in the user document
      const response = await AxiosInstance.put(`/user/updateuser`, {
        Roles: [{
          roleId: formData.newRole,
          roleName: getRoleNameById(formData.newRole),
          isActive: true,
          isAccountActive: true,
          isTeamActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }]
      }, { params: { UserId: formData.UserId } });
      
      if (response.data?.statusCode === 200) {
        showToast.success('User role updated successfully!');
        setShowUpdateModal(false);
        resetForm();
        fetchData();
      } else {
        showToast.error(response.data?.message || 'Failed to update user role');
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      // Toast notification is handled by AxiosInstance interceptor
    }
  };

  const resetForm = () => {
    setFormData({
      UserId: '',
      newRole: '',
      Notes: ''
    });
    setSelectedUser(null);
  };

  const openUpdateModal = (user) => {
    setSelectedUser(user);
    const currentRole = getUserCurrentRole(user);
    const newFormData = {
      UserId: user.UserId,
      newRole: currentRole?.roleId || '',
      Notes: ''
    };
    setFormData(newFormData);
    setShowUpdateModal(true);
  };

  const getUserCurrentRole = (user) => {
    if (user.Roles && user.Roles.length > 0) {
      const currentRole = user.Roles[0];
      return {
         roleId: currentRole.roleId,
        roleName: currentRole.roleName,
        isActive: currentRole.isActive
      };
    }
    return null;
  };

  const getRoleNameById = (roleId) => {
    const role = roles.find(r => r.RoleId === roleId);
    return role ? role.RoleName : 'Unknown Role';
  };

  // Filter and sort users
  const getFilteredAndSortedUsers = () => {
    let filteredUsers = users.filter(user => {
      const matchesSearch = 
        user.FirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.LastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.EmailAddress?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const currentRole = getUserCurrentRole(user);
      const matchesRole = roleFilter === 'all' || 
        (currentRole && currentRole.roleName.toLowerCase() === roleFilter.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || 
        (currentRole && currentRole.isActive === (statusFilter === 'active'));
      
      return matchesSearch && matchesRole && matchesStatus;
    });

    // Sort users
    filteredUsers.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = `${a.FirstName} ${a.LastName}`.toLowerCase();
          bValue = `${b.FirstName} ${b.LastName}`.toLowerCase();
          break;
        case 'email':
          aValue = a.EmailAddress?.toLowerCase() || '';
          bValue = b.EmailAddress?.toLowerCase() || '';
          break;
        case 'role':
          aValue = getUserCurrentRole(a)?.roleName?.toLowerCase() || '';
          bValue = getUserCurrentRole(b)?.roleName?.toLowerCase() || '';
          break;
        default:
          aValue = a.FirstName?.toLowerCase() || '';
          bValue = b.FirstName?.toLowerCase() || '';
      }
      
      if (sortOrder === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    return filteredUsers;
  };

  const getStatusIcon = (isActive) => {
    if (isActive === undefined) return <FiClock className="status-icon pending" />;
    return isActive ? 
      <FiCheckCircle className="status-icon active" /> : 
      <FiAlertCircle className="status-icon inactive" />;
  };

  const getRoleColor = (roleName) => {
    const roleColors = {
      'admin': '#ef4444',
      'superadmin': '#7c3aed',
      'member': '#10b981',
      'user': '#3b82f6',
      'moderator': '#f59e0b'
    };
    return roleColors[roleName?.toLowerCase()] || '#6b7280';
  };

  if (loading) {
    return (
      <div className="user-role-assignment">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users and roles...</p>
        </div>
      </div>
    );
  }

  const filteredUsers = getFilteredAndSortedUsers();
  const totalUsers = users.length;
  const usersWithRoles = users.filter(user => getUserCurrentRole(user)).length;
  const availableRoles = roles.length;

  return (
    <div className="user-role-assignment">
      {/* Enhanced Header */}
      <div className="assignment-header">
        <div className="assignment-header-left">
          <div className="header-icon-container">
            <FiUsers className="assignment-icon" />
          </div>
          <div className="header-content">
            <h1>User Role Management</h1>
            <p className="header-subtitle">Manage user roles and permissions across your organization</p>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className="refresh-btn"
            onClick={() => fetchData()}
            title="Refresh data"
          >
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="assignment-stats">
        <div className="stat-card total-users">
          <div className="stat-icon">
            <FiUsers />
          </div>
          <div className="stat-content">
            <h3>{totalUsers}</h3>
            <p>Total Users</p>
            <span className="stat-trend positive">+{Math.floor(totalUsers * 0.1)} this month</span>
          </div>
        </div>
        
        <div className="stat-card users-with-roles">
          <div className="stat-icon">
            <FiCheckCircle />
          </div>
          <div className="stat-content">
            <h3>{usersWithRoles}</h3>
            <p>Users with Roles</p>
            <span className="stat-trend positive">{Math.round((usersWithRoles / totalUsers) * 100)}% coverage</span>
          </div>
        </div>
        
        <div className="stat-card available-roles">
          <div className="stat-icon">
            <FiShield />
          </div>
          <div className="stat-content">
            <h3>{availableRoles}</h3>
            <p>Available Roles</p>
            <span className="stat-trend neutral">Customizable permissions</span>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="search-container">
          <div className="search-input-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
        
        <div className="filter-container">
          <div className="filter-group">
            <label>Role:</label>
            <select 
              value={roleFilter} 
              onChange={(e) => setRoleFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Roles</option>
              {roles.map(role => (
                <option key={role.RoleId} value={role.RoleName.toLowerCase()}>
                  {role.RoleDisplayName}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Status:</label>
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Sort by:</label>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="name">Name</option>
              <option value="email">Email</option>
              <option value="role">Role</option>
            </select>
          </div>
          
          <button 
            className={`sort-order-btn ${sortOrder === 'asc' ? 'asc' : 'desc'}`}
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            title={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Enhanced Users Section */}
      <div className="users-section">
        <div className="section-header">
          <h2>All Users ({filteredUsers.length})</h2>
          <div className="section-actions">
            <span className="results-info">
              Showing {filteredUsers.length} of {totalUsers} users
            </span>
          </div>
        </div>
        
        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <FiUsers size={64} />
            </div>
            <h3>No users found</h3>
            <p>
              {searchTerm || roleFilter !== 'all' || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'No users are available for role management'
              }
            </p>
            {(searchTerm || roleFilter !== 'all' || statusFilter !== 'all') && (
              <button 
                className="clear-filters-btn"
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('all');
                  setStatusFilter('all');
                }}
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => setSortBy('name')}>
                    User Name
                    {sortBy === 'name' && (
                      <span className="sort-indicator">
                        {sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => setSortBy('email')}>
                    Email
                    {sortBy === 'email' && (
                      <span className="sort-indicator">
                        {sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => setSortBy('role')}>
                    Current Role
                    {sortBy === 'role' && (
                      <span className="sort-indicator">
                        {sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const currentRole = getUserCurrentRole(user);
                  return (
                    <tr key={user.UserId} className="user-row">
                      <td className="user-name">
                        <div className="user-info-cell">
                          <div className="user-avatar">
                            {user.FirstName?.[0]}{user.LastName?.[0]}
                          </div>
                          <div className="user-details">
                            <h4>{user.FirstName} {user.LastName}</h4>
                            <span className="user-id">ID: {user.UserId}</span>
                          </div>
                        </div>
                      </td>
                      <td className="user-email">
                        <div className="email-cell">
                          <span className="email-text">{user.EmailAddress}</span>
                        </div>
                      </td>
                      <td className="user-role">
                        {currentRole ? (
                          <div className="role-cell">
                            <span 
                              className="role-badge"
                              style={{ backgroundColor: getRoleColor(currentRole.roleName) }}
                            >
                              {currentRole.roleName.toUpperCase()}
                            </span>
                            {(() => {
                              const desc = roles.find(r => r.RoleId === currentRole.roleId)?.Description || 'No description';
                              return (
                                <Tooltip content={desc} placement="top">
                                  <span className="role-eye" aria-label="View role description">
                                    <FiEye />
                                  </span>
                                </Tooltip>
                              );
                            })()}
                          </div>
                        ) : (
                          <span className="no-role">No role assigned</span>
                        )}
                      </td>
                      <td className="user-status">
                        {currentRole ? (
                          <div className="status-cell">
                            <span className={`status-badge ${currentRole.isActive ? 'active' : 'inactive'}`}>
                            {getStatusIcon(currentRole.isActive)}{currentRole.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        ) : (
                          <div className="status-cell">
                            {getStatusIcon(undefined)}
                            <span className="status-badge pending">Pending</span>
                          </div>
                        )}
                      </td>
                      <td className="user-actions">
                        <div className="action-buttons">
                          {/* <button 
                            className="view-btn"
                            title="View user details"
                          >
                            <FiEye />
                          </button> */}
                          <button 
                            className="update-role-btn"
                            onClick={() => openUpdateModal(user)}
                            title="Update user role"
                          >
                            <FiEdit />
                            Update Role
                          </button>
                          {/* <button 
                            className="more-btn"
                            title="More options"
                          >
                            <FiMoreVertical />
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Enhanced Update Role Modal */}
      {showUpdateModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-title">
                <div className="modal-user-info">
                  <div className="modal-user-avatar">
                    {selectedUser.FirstName?.[0]}{selectedUser.LastName?.[0]}
                  </div>
                  <div>
                    <h3>Update Role</h3>
                    <p>{selectedUser.FirstName} {selectedUser.LastName}</p>
                  </div>
                </div>
              </div>
              <button 
                className="close-btn"
                onClick={() => setShowUpdateModal(false)}
              >
                <FiX />
              </button>
            </div>
            
            <form onSubmit={handleUpdateUserRole} className="modal-form">
              <div className="form-section">
                <h4>Current Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>User</label>
                    <input
                      type="text"
                      value={`${selectedUser.FirstName} ${selectedUser.LastName} (${selectedUser.EmailAddress})`}
                      disabled
                      className="disabled-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Current Role</label>
                    <input
                      type="text"
                      value={getUserCurrentRole(selectedUser)?.roleName || 'No role assigned'}
                      disabled
                      className="disabled-input"
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-section">
                <h4>New Role Assignment</h4>
                <div className="form-group">
                  <label>Select New Role <span className="required">*</span></label>
                  <CustomDropdown
                    options={(() => {
                      // Ensure only company-specific roles are shown
                      const userData = config.getUserData();
                      const userCompanyId = userData?.CompanyId;
                      const userRole = config.getUserRole();
                      const isSuperAdmin = userRole === 'SuperAdmin' || userRole === 'superadmin';
                      
                      // Filter roles by selected user's company
                      // For normal users: roles are already filtered by their company in fetchData
                      // For SuperAdmin: show all roles, but filter by selected user's company to ensure correct assignment
                      let filteredRoles = roles;
                      if (selectedUser && selectedUser.CompanyId) {
                        if (isSuperAdmin) {
                          // SuperAdmin can see all roles, but filter by selected user's company for assignment
                          filteredRoles = roles.filter(role => role.CompanyId === selectedUser.CompanyId);
                        } else {
                          // Normal users: roles are already filtered by their company, but double-check
                          filteredRoles = roles.filter(role => role.CompanyId === selectedUser.CompanyId);
                        }
                      }
                      
                      const roleOptions = filteredRoles.map((role) => ({
                        value: role.RoleId,
                        label: `${role.RoleDisplayName} (${role.RoleName})`,
                        data: role
                      }));
                      return roleOptions;
                    })()}
                    value={(() => {
                      const selectedOption = roles.find(role => role.RoleId === formData.newRole);
                      return selectedOption ? {
                        value: selectedOption.RoleId,
                        label: `${selectedOption.RoleDisplayName} (${selectedOption.RoleName})`,
                        data: selectedOption
                      } : null;
                    })()}
                    onChange={(option) => {
                      setFormData({...formData, newRole: option.value});
                    }}
                    placeholder="Choose a new role..."
                    className="role-select"
                    searchable={true}
                    textColor="white"
                  />
                  {formData.newRole && (
                    <div className="role-info">
                      <p className="role-description">
                        {roles.find(r => r.RoleId === formData.newRole)?.Description || 'No description available'}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Notes (Optional)</label>
                  <textarea
                    value={formData.Notes}
                    onChange={(e) => setFormData({...formData, Notes: e.target.value})}
                    placeholder="Add any notes about this role update..."
                    rows="3"
                    className="notes-textarea"
                  />
                </div>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => setShowUpdateModal(false)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button type="submit" className="primary-btn">
                  <FiSave />
                  Update Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRoleAssignment; 